/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 補足対応の起票フォーム（/ops/submit?followUpOf=<id>）の元依頼まわり。元依頼を取得して、完了済みのダッシュボード起票の依頼ならフォームへ引き継ぐ値（サービス・対象ユーザー・背景・WFID・CS チケット URL）を返し、取得中・取得失敗（再試行）・起票できない元依頼の表示と、フォーム上部の「#N の補足対応」バナーを出す。種別と対応依頼は引き継がず、起票者が選び直す
 * @graph-connects useOpsRequest [calls] 元依頼の取得
 * @graph-connects canCreateOpsFollowUp [calls] 補足対応を起票できる元依頼か判定
 * @graph-connects opsFollowUpSeed [calls] フォームへ引き継ぐ値の取り出し
 * @graph-connects OpsFetchErrorPanel [references] 取得失敗の表示
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import {
  canCreateOpsFollowUp,
  opsDisplayNo,
  opsFollowUpSeed,
  type OpsFollowUpSeed,
} from '@cortex/ops-core';
import { AccentBox } from './OpsPanels.js';
import { OpsFetchErrorPanel } from './OpsDashboardStates.js';
import { useOpsRequest } from '../../../hooks/useOps.js';
import type { OpsRequest } from '../../../types/ops.js';

/** フォームへ渡す補足対応の元依頼（取得・検証済み）と引き継ぐ値。 @graph-connects none */
export interface OpsFollowUpOrigin {
  /** 元依頼。 */
  origin: OpsRequest;
  /** フォームへ引き継ぐ値。 */
  seed: OpsFollowUpSeed;
}

/**
 * 補足対応の元依頼の状態。`none` は通常の起票（followUpOf なし）、`ready` はフォームを出せる状態。
 * @graph-connects none
 */
export type OpsFollowUpOriginState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  | { status: 'invalid' }
  | ({ status: 'ready' } & OpsFollowUpOrigin);

/**
 * followUpOf の元依頼を取得し、補足対応を起票できるか判定する。followUpOf が無ければ取得しない。
 * @graph-connects useOpsRequest [calls] 元依頼の取得
 * @graph-connects canCreateOpsFollowUp [calls] 補足対応を起票できる元依頼か判定
 * @graph-connects opsFollowUpSeed [calls] フォームへ引き継ぐ値の取り出し
 */
export function useOpsFollowUpOrigin(followUpOf: string | undefined): OpsFollowUpOriginState {
  const query = useOpsRequest(followUpOf ?? '', followUpOf !== undefined);
  if (followUpOf === undefined) return { status: 'none' };
  if (query.isError) {
    return {
      status: 'error',
      retry: () => {
        void query.refetch();
      },
    };
  }
  if (query.data === undefined) return { status: 'loading' };
  const origin = query.data;
  if (origin === null || !canCreateOpsFollowUp(origin)) return { status: 'invalid' };
  return { status: 'ready', origin, seed: opsFollowUpSeed(origin) };
}

/** フォームを出す前の状態（取得中・取得失敗・起票できない元依頼）の props。 */
export interface OpsFollowUpOriginPendingProps {
  /** 元依頼の状態（ready / none 以外）。 */
  state: Exclude<OpsFollowUpOriginState, { status: 'none' } | { status: 'ready' }>;
}

/**
 * 元依頼の取得中・取得失敗・起票できない元依頼の表示。起票できないときは通常の起票フォームへの導線を出す。
 * @graph-connects OpsFetchErrorPanel [references] 取得失敗の表示
 */
export function OpsFollowUpOriginPending({ state }: OpsFollowUpOriginPendingProps): ReactNode {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader title={t('ops.submit.followUp.title')} />
      <CardContent>
        {state.status === 'loading' && (
          <p className="py-6 text-center text-sm text-[var(--color-text-muted)]">
            {t('ops.submit.followUp.loading')}
          </p>
        )}
        {state.status === 'error' && <OpsFetchErrorPanel onRetry={state.retry} />}
        {state.status === 'invalid' && (
          <AccentBox tone="warning" title={t('ops.submit.followUp.invalidTitle')}>
            {t('ops.submit.followUp.invalidBody')}{' '}
            <Link
              to="/ops/submit"
              className="font-semibold text-[var(--color-accent-primary)] hover:underline"
            >
              {t('ops.submit.followUp.newRequest')}
            </Link>
          </AccentBox>
        )}
      </CardContent>
    </Card>
  );
}

/** 補足対応バナーの props。 */
export interface OpsFollowUpBannerProps {
  /** 元依頼。 */
  origin: OpsRequest;
}

/**
 * フォーム上部の「#N の補足対応として起票します」バナー（元依頼へのリンクつき）。
 * @graph-connects opsDisplayNo [calls] 元依頼の表示用 No
 */
export function OpsFollowUpBanner({ origin }: OpsFollowUpBannerProps): ReactNode {
  const { t } = useTranslation();
  return (
    <AccentBox tone="info" title={t('ops.submit.followUp.bannerTitle')}>
      <Link
        to="/ops/requests/$id"
        params={{ id: origin.id }}
        className="font-semibold text-[var(--color-accent-primary)] hover:underline"
      >
        #{opsDisplayNo(origin.id)}
      </Link>{' '}
      {t('ops.submit.followUp.bannerBody')}
    </AccentBox>
  );
}
